import { Link } from "react-router-dom";
import { Mail, FileText, Info, MessageSquare, Shield, HelpCircle, DollarSign, ArrowRight, Sparkles, ClipboardCheck, Gauge, Lock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { DeleteMyDataDialog } from "@/components/DeleteMyDataDialog";


export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-header text-white border-t border-white/10 mt-auto">
      {/* Document Pack mini-banner */}
      <div className="border-b border-white/10 bg-accent/5">
        <div className="container mx-auto px-4 py-3">
          <Link to="/documents" className="flex items-center justify-center gap-2 text-sm text-white/80 hover:text-accent transition-colors">
            <FileText className="h-4 w-4 text-accent" />
            <span><strong>{t('footer.docPack.banner')}</strong> - {t('footer.docPack.tagline')} - <span className="text-accent font-semibold">{t('footer.docPack.price')}</span></span>
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Product */}
          <FooterColumn title="Product">
            <FooterLink to="/pricing" icon={DollarSign} label="Pricing" />
            <FooterLink to="/documents" icon={FileText} label="Documents" />
            <FooterLink to="/audit-room" icon={ClipboardCheck} label="Audit Room" />
            <FooterLink to="/ai-assistant" icon={Sparkles} label="AI Assistant" />
            <FooterLink to="/readiness-check" icon={Gauge} label="Readiness Check" />
            <FooterLink to="/risk-management" icon={Shield} label="Risk Copilot" />
          </FooterColumn>

          {/* Learn */}
          <FooterColumn title="Learn">
            <FooterLink to="/blog" icon={FileText} label="Blog" />
            <FooterLink to="/iso-27001-annex-a-controls" icon={Shield} label="Annex A Controls" />
            <FooterLink to="/iso-27001-gap-analysis" icon={FileText} label="ISO 27001 Gap Analysis" />
            <FooterLink to="/iso-27001-templates" icon={FileText} label="ISO 27001 Templates" />
            <FooterLink to="/faq" icon={HelpCircle} label="FAQ" />
          </FooterColumn>

          {/* Company */}
          <FooterColumn title="Company">
            <FooterLink to="/about" icon={Info} label="About" />
            <FooterLink to="/consultation" icon={MessageSquare} label="Consultation" />
            <FooterLink to="/iso-27001-implementation-review" icon={ClipboardCheck} label="Implementation Review" />
            <FooterLink to="/contact" icon={Mail} label="Contact" />
            <li>
              <a
                href="mailto:contact@iso27001kit.com"
                className="text-sm text-white/60 hover:text-accent transition-colors flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                contact@iso27001kit.com
              </a>
            </li>
          </FooterColumn>

          {/* Trust & Legal */}
          <FooterColumn title="Trust and legal">
            <FooterLink to="/security" icon={Lock} label="Security" />
            <FooterLink to="/privacy-policy" icon={Shield} label="Privacy Policy" />
            <FooterLink to="/terms-of-service" icon={FileText} label="Terms" />
            <FooterLink to="/refund-policy" icon={FileText} label="Refund Policy" />
            <FooterLink to="/disclaimer" icon={Info} label="Disclaimer" />
            <li>
              <DeleteMyDataDialog />
            </li>
          </FooterColumn>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">
              {t('footer.copyright')}
            </p>
            <div className="flex flex-wrap justify-center md:justify-end gap-x-4 gap-y-2 text-sm text-white/60">
              <Link to="/security" className="hover:text-accent transition-colors">Security</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/privacy-policy" className="hover:text-accent transition-colors">{t('footer.privacyPolicy')}</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/terms-of-service" className="hover:text-accent transition-colors">{t('footer.termsOfService')}</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/refund-policy" className="hover:text-accent transition-colors">{t('footer.refundPolicy')}</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/disclaimer" className="hover:text-accent transition-colors">{t('footer.disclaimer')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 text-accent">{title}</h3>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function FooterLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-white/80 hover:text-accent transition-colors flex items-center gap-2"
      >
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    </li>
  );
}
